import { type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { type Brand } from '@jsmdg/tenancy';
import {
    EarthOutlineIcon,
    Link,
    LinkVariant,
    MailOutlineIcon,
    PhoneOutlineIcon,
} from '@jsmdg/yoshi';
import { type ProductDetailContext } from '../../types/types';
import Address from './Address';
import { trackPhoneNumberClicked } from './serviceConsultant.tracking';
import styles from './ServiceConsultant.module.scss';

const messages = defineMessages({
    headlineB2C: {
        defaultMessage: 'Du hast noch Fragen?',
    },
    normalServiceHoursTitle: {
        defaultMessage: 'Du erreichst uns telefonisch zu folgenden Zeiten:',
    },
    christmasServiceHoursTitleStarts: {
        defaultMessage: 'Verlängerte Servicezeiten ab dem',
    },
    christmasServiceHoursTitleEnd: {
        defaultMessage: '1. Adventswochenende:',
    },
    headlineB2B: {
        defaultMessage: 'Firmenevent oder Firmenbestellung geplant?',
    },
    serviceHoursB2B: {
        defaultMessage: 'Mo-Fr: 9-17 Uhr',
    },
});

const createRow = (firstElement: ReactNode, secondElement?: ReactNode): JSX.Element => {
    return (
        <div className="d-flex mt-2x">
            <div className="container-fluid">
                <div className="grid">
                    <div className={classNames('g-col-12', { 'g-col-sm-6': secondElement })}>
                        {firstElement}
                    </div>
                    {secondElement && (
                        <div className="g-col-12 g-col-sm-6 mt-2x mt-sm-0">{secondElement}</div>
                    )}
                </div>
            </div>
        </div>
    );
};

const createPhoneNumber = (
    phoneNumber: string,
    eventLabel?: string,
    className?: string,
): JSX.Element => {
    return (
        <div className={classNames('d-flex', className)}>
            <PhoneOutlineIcon className="mr-1x flex-shrink-0" />
            <span className="d-none d-sm-block">{phoneNumber}</span>
            <Link
                href={`tel:${phoneNumber}`}
                className="d-sm-none"
                onClick={() => {
                    trackPhoneNumberClicked(eventLabel || '');
                }}
                variant={LinkVariant.Grey}
            >
                {phoneNumber}
            </Link>
        </div>
    );
};

type ServiceConsultantProps = {
    readonly brand?: Brand;
};

const ServiceConsultant = ({ brand }: ServiceConsultantProps): JSX.Element | null => {
    const intl = useIntl();
    const { tenantConfig } = useFragmentContext<ProductDetailContext>();

    if (!brand) {
        return null;
    }

    return (
        <div className={classNames(styles.panel, 'p-2x m-0')}>
            <div className="theme-text-subtitle-text">
                {intl.formatMessage(messages.headlineB2C)}
            </div>
            {createRow(
                <div>
                    {createPhoneNumber(tenantConfig.contactInfo.b2cPhoneNumber, 'Phone', 'mt-2x')}
                    <div className="d-flex mt-2x">
                        <MailOutlineIcon className="mr-1x flex-shrink-0" />
                        <span className="d-sm-block">{tenantConfig.contactInfo.serviceEmail}</span>
                    </div>
                </div>,
                <Address />,
            )}

            {tenantConfig.serviceHours.normalServiceHours ? (
                <div className="theme-text-body-text mt-2x">
                    {intl.formatMessage(messages.normalServiceHoursTitle)}
                    <span className="d-block mt-1x mt-sm-1x">
                        {tenantConfig.serviceHours.normalServiceHours}
                    </span>
                </div>
            ) : null}
            {tenantConfig.serviceHours.christmasServiceHours ? (
                <div className="theme-text-body-text mt-2x">
                    {intl.formatMessage(messages.christmasServiceHoursTitleStarts)}{' '}
                    <span className="fw-bold text-nowrap">
                        {intl.formatMessage(messages.christmasServiceHoursTitleEnd)}
                    </span>
                    <span className="d-block mt-1x mt-sm-1x">
                        {tenantConfig.serviceHours.christmasServiceHours}
                    </span>
                </div>
            ) : null}
            <hr className="my-3x" />
            <div className="theme-text-subtitle-text">
                {intl.formatMessage(messages.headlineB2B)}
            </div>
            <div className="mt-1x">{tenantConfig.b2bTeam}</div>
            {createRow(
                createPhoneNumber(tenantConfig.contactInfo.b2bPhoneNumber, 'B2B_Phone'),
                <div className="ml-4x ml-sm-0">{intl.formatMessage(messages.serviceHoursB2B)}</div>,
            )}
            {createRow(
                <div className="d-flex">
                    <EarthOutlineIcon className="mr-1x flex-shrink-0" />
                    <Link href={tenantConfig.urls.b2bWebsite} variant={LinkVariant.Grey}>
                        {tenantConfig.b2bWebsiteText}
                    </Link>
                </div>,
            )}
        </div>
    );
};

export { ServiceConsultant };
